<template>
  <div class="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
    <!-- Animated Background Elements (Client-only for better SSR) -->
    <div class="fixed inset-0 overflow-hidden pointer-events-none" v-if="isClient">
      <div class="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-20 animate-pulse"></div>
      <div class="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-200 to-pink-200 rounded-full opacity-20 animate-pulse" style="animation-delay: 2s;"></div>
      <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full opacity-10 animate-pulse" style="animation-delay: 4s;"></div>
    </div>

    <!-- Navigation Header -->
    <nav class="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <!-- Logo and Title -->
          <div class="flex items-center">
            <div class="flex-shrink-0 flex items-center">
              <!-- QR Code Logo -->
              <div class="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                <IconifyIcon icon="tabler:qr-code" class="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 class="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  WebRTC QR Tool
                </h1>
                <p class="text-xs text-gray-500">Serverless P2P Connections</p>
              </div>
            </div>
          </div>

          <!-- Navigation Links -->
          <div class="hidden md:flex items-center space-x-1">
            <router-link
              to="/"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': typeof $route.name === 'string' && $route.name === 'home' }"
            >
              <span class="relative z-10">Home</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'home' }"></div>
            </router-link>
            <router-link
              to="/demo"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': typeof $route.name === 'string' && $route.name?.startsWith('demo') }"
            >
              <span class="relative z-10">Demo</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': typeof $route.name === 'string' && $route.name?.startsWith && $route.name.startsWith('demo') }"></div>
            </router-link>
            <router-link
              to="/generator"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': $route.name === 'generator' }"
            >
              <span class="relative z-10">Generator</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'generator' }"></div>
            </router-link>
            <router-link
              to="/scanner"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': $route.name === 'scanner' }"
            >
              <span class="relative z-10">Scanner</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'scanner' }"></div>
            </router-link>
            <router-link
              to="/chat"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': $route.name === 'chat' }"
            >
              <span class="relative z-10">Chat</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'chat' }"></div>
            </router-link>
            <router-link
              to="/video-conference"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': typeof $route.name === 'string' && ($route.name === 'video-conference' || $route.name === 'join-room') }"
            >
              <span class="relative z-10">Video Conference</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': typeof $route.name === 'string' && ($route.name === 'video-conference' || $route.name === 'join-room') }"></div>
            </router-link>
            <router-link
              to="/multi-video-conference"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': typeof $route.name === 'string' && ($route.name === 'multi-video-conference' || $route.name === 'multi-video-conference-join') }"
            >
              <span class="relative z-10">Multi-Conference</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': typeof $route.name === 'string' && ($route.name === 'multi-video-conference' || $route.name === 'multi-video-conference-join') }"></div>
            </router-link>
            <router-link
              to="/file-transfer"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': $route.name === 'file-transfer' }"
            >
              <span class="relative z-10">File Transfer</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'file-transfer' }"></div>
            </router-link>
            <router-link
              to="/docs"
              class="relative group text-gray-700 hover:text-indigo-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-indigo-50"
              :class="{ 'text-indigo-600 bg-indigo-50': $route.name === 'docs' }"
            >
              <span class="relative z-10">Documentation</span>
              <div class="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" :class="{ 'scale-x-100': $route.name === 'docs' }"></div>
            </router-link>
          </div>

          <!-- Action Buttons -->
          <div class="hidden md:flex items-center space-x-2">
            <a
              href="https://github.com/example/webrtc-qr-tool"
              target="_blank"
              rel="noopener noreferrer"
              class="group p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:scale-110"
              title="View on GitHub"
            >
              <IconifyIcon icon="octicon:mark-github-24" class="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" />
            </a>
            <button
              @click="toggleTheme"
              class="group p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 transform hover:scale-110"
              title="Toggle theme"
            >
              <IconifyIcon icon="heroicons:moon-20-solid" class="w-5 h-5 transition-transform duration-200 group-hover:rotate-180" />
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile menu button -->
      <div class="md:hidden flex items-center">
        <button
          @click="toggleMobileMenu"
          class="relative w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200 group"
          aria-label="Toggle mobile menu"
        >
          <!-- Hamburger Icon -->
          <div class="absolute w-6 h-6 transition-all duration-300" :class="{ 'rotate-45 translate-y-0': mobileMenuOpen, 'rotate-0 translate-y-1': !mobileMenuOpen }">
            <div class="absolute w-6 h-0.5 bg-current transition-all duration-300" :class="{ 'rotate-45 translate-y-0': mobileMenuOpen, '-rotate-0 -translate-y-2': !mobileMenuOpen }"></div>
            <div class="absolute w-6 h-0.5 bg-current top-1/2 -translate-y-1/2 transition-all duration-300" :class="{ 'opacity-0': mobileMenuOpen, 'opacity-100': !mobileMenuOpen }"></div>
            <div class="absolute w-6 h-0.5 bg-current transition-all duration-300" :class="{ 'rotate-45 translate-y-0': mobileMenuOpen, 'rotate-0 translate-y-2': !mobileMenuOpen }"></div>
          </div>
        </button>
      </div>
    </nav>

    <!-- Mobile menu with enhanced animation -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform -translate-y-2 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div v-if="mobileMenuOpen" class="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 shadow-xl">
        <div class="px-2 pt-4 pb-6 space-y-1">
          <router-link
            to="/"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:home-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Home</span>
            </div>
          </router-link>
          <router-link
            to="/demo"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:play-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Demo</span>
            </div>
          </router-link>
          <router-link
            to="/generator"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:squares-2x2-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Generator</span>
            </div>
          </router-link>
          <router-link
            to="/scanner"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:camera-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Scanner</span>
            </div>
          </router-link>
          <router-link
            to="/chat"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:chat-bubble-left-right-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Chat</span>
            </div>
          </router-link>
          <router-link
            to="/video-conference"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:video-camera-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Video Conference</span>
            </div>
          </router-link>
          <router-link
            to="/multi-video-conference"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:users-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Multi-Conference</span>
            </div>
          </router-link>
          <router-link
            to="/file-transfer"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:arrow-up-tray-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>File Transfer</span>
            </div>
          </router-link>
          <router-link
            to="/docs"
            class="group block px-4 py-3 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 font-medium rounded-lg transition-all duration-200 transform hover:translate-x-2"
            @click="mobileMenuOpen = false"
          >
            <div class="flex items-center space-x-3">
              <IconifyIcon icon="heroicons:book-open-20-solid" class="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
              <span>Documentation</span>
            </div>
          </router-link>
        </div>

        <!-- Mobile Action Buttons -->
        <div class="px-4 py-3 border-t border-gray-100 flex items-center justify-center space-x-4">
          <a
            href="https://github.com/example/webrtc-qr-tool"
            target="_blank"
            rel="noopener noreferrer"
            class="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
            title="View on GitHub"
          >
            <IconifyIcon icon="octicon:mark-github-24" class="w-5 h-5" />
          </a>
          <button
            @click="toggleTheme"
            class="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
            title="Toggle theme"
          >
            <IconifyIcon icon="heroicons:moon-20-solid" class="w-5 h-5" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Main Content -->
    <main class="flex-1">
      <router-view v-slot="{ Component, route }">
        <transition
          name="fade"
          mode="out-in"
          appear
        >
          <component :is="Component" :key="route.path" />
        </transition>
      </router-view>
    </main>

    <!-- Footer -->
    <footer class="bg-gray-50 border-t">
      <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col md:flex-row justify-between items-center">
          <div class="text-center md:text-left mb-4 md:mb-0">
            <p class="text-gray-600 text-sm">
              Built with ❤️ using
              <a href="https://www.rust-lang.org" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-700">Rust</a>
              +
              <a href="https://vuejs.org" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-700">Vue.js</a>
              +
              <a href="https://webrtc.org" target="_blank" rel="noopener noreferrer" class="text-indigo-600 hover:text-indigo-700">WebRTC</a>
            </p>
          </div>
          <div class="flex items-center space-x-4">
            <span class="text-gray-500 text-sm">
              Version: {{ version }}
            </span>
            <a
              href="https://github.com/example/webrtc-qr-tool"
              target="_blank"
              rel="noopener noreferrer"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <IconifyIcon icon="octicon:mark-github-24" class="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import IconifyIcon from './components/IconifyIcon.vue'
import { performanceMonitor, logPerformanceMetrics } from './utils/performance'
import { mobileEnhancer } from './utils/mobile'
// import { version } from '../pkg/webrtc_qr_tool'
const version = '0.1.0'

// SSR client detection
const isClient = ref(false)
const mobileMenuOpen = ref(false)

const toggleTheme = () => {
  // Theme toggle implementation
  document.documentElement.classList.toggle('dark')
}

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

// Close mobile menu when clicking outside
const handleClickOutside = (event: Event) => {
  if (mobileMenuOpen.value && !(event.target as Element).closest('nav')) {
    mobileMenuOpen.value = false
  }
}

onMounted(() => {
  // Client-side hydration
  isClient.value = true

  // Initialize performance monitoring
  setTimeout(() => {
    logPerformanceMetrics()

    // Log performance grade
    const grade = performanceMonitor.getPerformanceGrade()
    const score = performanceMonitor.getPerformanceScore()
    console.log(`🚀 Performance Grade: ${grade} (${score}/100)`)
  }, 1000)

  // Initialize mobile enhancements
  const device = mobileEnhancer.getDevice()
  const deviceType = device.isMobile ? 'mobile' : device.isTablet ? 'tablet' : 'desktop'
  console.log(`📱 Device: ${deviceType} (${device.orientation}) - ${device.connectionType}`)

  // Initialize any global state
  console.log('🌟 WebRTC QR Tool App mounted with performance monitoring')
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style scoped>
/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>